import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import type { TabDefinition, TabRegistryEntry } from "@/types/tabs";

interface TabContextValue {
  tabs: TabDefinition[];
  activeTabId: string | null;
  openTab: (path: string, label?: string) => void;
  closeTab: (tabId: string) => void;
  switchTab: (tabId: string) => void;
  updateTabLabel: (tabId: string, label: string) => void;
  registry: TabRegistryEntry[];
}

const TabContext = createContext<TabContextValue | null>(null);

export function useTabContext(): TabContextValue {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error("useTabContext must be used within a TabProvider");
  return ctx;
}

/** Try to use tab context, returns null if outside a TabProvider */
export function useOptionalTabContext(): TabContextValue | null {
  return useContext(TabContext);
}

// ── helpers ──────────────────────────────────────────────────────────

function matchRegistry(
  path: string,
  registry: TabRegistryEntry[],
): { entry: TabRegistryEntry; params: Record<string, string> } | null {
  for (const entry of registry) {
    const m = matchPath(entry.routePattern, path);
    if (m) return { entry, params: m.params as Record<string, string> };
  }
  return null;
}

/** Strip query params for tab identity — same page with different ?tab= is still one tab */
function buildTabId(path: string): string {
  return path.split("?")[0];
}

function buildTabDef(
  path: string,
  entry: TabRegistryEntry,
  params: Record<string, string>,
  labelOverride?: string,
): TabDefinition {
  const label =
    labelOverride ??
    (typeof entry.label === "function" ? entry.label(params) : entry.label);
  return {
    id: buildTabId(path),
    routePattern: entry.routePattern,
    params,
    label,
    icon: entry.icon,
    closable: entry.closable,
    path,
  };
}

// ── provider ─────────────────────────────────────────────────────────

interface TabProviderProps {
  registry: TabRegistryEntry[];
  children: React.ReactNode;
}

const TAB_STORAGE_KEY = "commandx_open_tabs";

function persistTabs(tabs: TabDefinition[], activeId: string | null) {
  try {
    sessionStorage.setItem(TAB_STORAGE_KEY, JSON.stringify({ tabs, activeId }));
  } catch { /* non-blocking */ }
}

function loadPersistedTabs(): { tabs: TabDefinition[]; activeId: string | null } | null {
  try {
    const raw = sessionStorage.getItem(TAB_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function TabProvider({ registry, children }: TabProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState<TabDefinition[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const initialised = useRef(false);
  // Track paths we navigate to ourselves so the sync effect ignores them
  const internalNav = useRef<string | null>(null);

  // On mount: restore persisted tabs or create one for current URL
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    const fullPath = location.pathname + location.search;
    internalNav.current = location.pathname;

    // Try to restore from session storage
    const persisted = loadPersistedTabs();
    if (persisted && persisted.tabs.length > 0) {
      const validTabs: TabDefinition[] = [];
      for (const pt of persisted.tabs) {
        const pathOnly = pt.path.split("?")[0];
        const match = matchRegistry(pathOnly, registry);
        if (match) {
          validTabs.push(buildTabDef(pt.path, match.entry, match.params, pt.label));
        }
      }
      if (validTabs.length > 0) {
        // Ensure current URL has a tab
        const currentTabId = buildTabId(fullPath);
        const currentTab = validTabs.find((t) => t.id === currentTabId);
        if (currentTab) {
          // Update the tab's path to include current search params
          currentTab.path = fullPath;
        } else {
          const match = matchRegistry(location.pathname, registry);
          if (match) {
            validTabs.push(buildTabDef(fullPath, match.entry, match.params));
          }
        }
        setTabs(validTabs);
        const activeTab = validTabs.find((t) => t.id === currentTabId)
          ?? validTabs.find((t) => t.id === persisted.activeId)
          ?? validTabs[0];
        setActiveTabId(activeTab?.id ?? null);
        if (activeTab && activeTab.path !== fullPath) {
          internalNav.current = activeTab.path.split("?")[0];
          navigate(activeTab.path, { replace: true });
        }
        return;
      }
    }

    // No persisted tabs — create one for the current URL
    const match = matchRegistry(location.pathname, registry);
    if (match) {
      const tab = buildTabDef(fullPath, match.entry, match.params);
      setTabs([tab]);
      setActiveTabId(tab.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist tabs whenever they change
  useEffect(() => {
    if (initialised.current && tabs.length > 0) {
      persistTabs(tabs, activeTabId);
    }
  }, [tabs, activeTabId]);

  // Sync: when URL changes externally (back/forward), switch or open matching tab
  useEffect(() => {
    if (!initialised.current) return;
    const fullPath = location.pathname + location.search;
    // Skip if this URL change was triggered by us
    if (internalNav.current === location.pathname) {
      internalNav.current = null;
      // Still update the active tab's path with new search params
      const tabId = buildTabId(fullPath);
      setTabs((prev) => prev.map((t) => t.id === tabId ? { ...t, path: fullPath } : t));
      return;
    }
    const tabId = buildTabId(fullPath);
    const existing = tabs.find((t) => t.id === tabId);
    if (existing) {
      if (existing.id !== activeTabId) setActiveTabId(existing.id);
      // Update path with current search params
      setTabs((prev) => prev.map((t) => t.id === tabId ? { ...t, path: fullPath } : t));
      return;
    }
    // URL changed to a path not in tabs — open it (e.g., browser back/forward)
    const match = matchRegistry(location.pathname, registry);
    if (match) {
      const tab = buildTabDef(fullPath, match.entry, match.params);
      setTabs((prev) => [...prev, tab]);
      setActiveTabId(tab.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const navTo = useCallback(
    (path: string) => {
      internalNav.current = path.split("?")[0]; // Store pathname only for sync effect comparison
      navigate(path, { replace: true });
    },
    [navigate],
  );

  const openTab = useCallback(
    (path: string, labelOverride?: string) => {
      // Strip query params for matching/ID but keep full path for navigation
      const pathOnly = path.split("?")[0];
      setTabs((prev) => {
        const existingIdx = prev.findIndex((t) => t.id === buildTabId(path));
        if (existingIdx >= 0) {
          // Already open — switch to it, update path with new query params
          const updated = [...prev];
          updated[existingIdx] = { ...updated[existingIdx], path };
          setActiveTabId(updated[existingIdx].id);
          navTo(path);
          return updated;
        }
        const match = matchRegistry(pathOnly, registry);
        if (!match) {
          // No registry entry — fall back to router navigation
          navigate(path);
          return prev;
        }
        const tab = buildTabDef(path, match.entry, match.params, labelOverride);
        setActiveTabId(tab.id);
        navTo(path);
        return [...prev, tab];
      });
    },
    [registry, navigate, navTo],
  );

  const closeTab = useCallback(
    (tabId: string) => {
      setTabs((prev) => {
        const idx = prev.findIndex((t) => t.id === tabId);
        if (idx < 0) return prev;
        const next = prev.filter((t) => t.id !== tabId);
        if (tabId === activeTabId && next.length > 0) {
          const newActive = next[Math.min(idx, next.length - 1)];
          setActiveTabId(newActive.id);
          navTo(newActive.path);
        }
        return next;
      });
    },
    [activeTabId, navTo],
  );

  const switchTab = useCallback(
    (tabId: string) => {
      setTabs((prev) => {
        const tab = prev.find((t) => t.id === tabId);
        if (!tab) return prev;
        setActiveTabId(tabId);
        navTo(tab.path);
        return prev;
      });
    },
    [navTo],
  );

  const updateTabLabel = useCallback(
    (tabId: string, label: string) => {
      setTabs((prev) =>
        prev.map((t) => (t.id === tabId ? { ...t, label } : t)),
      );
    },
    [],
  );

  const value = useMemo(
    () => ({ tabs, activeTabId, openTab, closeTab, switchTab, updateTabLabel, registry }),
    [tabs, activeTabId, openTab, closeTab, switchTab, updateTabLabel, registry],
  );

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
}
